import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { Button, Card, Spinner, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { QrCode, Trash2, CheckCircle2, AlertTriangle, AlertOctagon, Copy, RotateCcw } from "lucide-react";

const STORAGE_KEY = "estoque_conferencia_scans_v1";

export default function EstoqueConferencia() {
  const [scan, setScan] = useState("");
  const [scans, setScans] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); }
    catch { return []; }
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scans));
  }, [scans]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const addScan = (raw) => {
    const v = (raw || "").trim();
    if (!v) return;
    setScans((prev) => [...prev, v]);
    setScan("");
    setResult(null); // invalida resultado ao adicionar mais scans
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addScan(scan);
    }
  };

  const remove = (idx) => setScans((prev) => prev.filter((_, i) => i !== idx));
  const clearAll = () => { if (window.confirm("Limpar todas as etiquetas bipadas?")) { setScans([]); setResult(null); } };

  const finalizar = async () => {
    if (scans.length === 0) { setErr("Bipe ao menos uma etiqueta antes de finalizar."); return; }
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/estoque/conferencia", { scans });
      setResult(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  const counts = {};
  scans.forEach((s) => { counts[s] = (counts[s] || 0) + 1; });
  const uniqueScans = Object.keys(counts);

  const t = result?.totais || {};
  const diffZero = (t.diferenca_sacos || 0) === 0 && (t.faltando || 0) === 0 && (t.sobrando || 0) === 0;

  return (
    <Layout title="Conferência de Estoque" subtitle="Bipe todas as etiquetas (próprias e de terceiros) — o sistema aponta pallets faltantes ou sobrando">
      {err && <div className="mb-4 text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna 1: Scanner input + lista */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <QrCode size={18} className="text-brand-600" />
              <h3 className="font-heading text-base font-bold text-ink-900">Bipar etiquetas</h3>
            </div>
            <div className="flex gap-2">
              <input
                ref={inputRef}
                autoFocus
                value={scan}
                onChange={(e) => setScan(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Aproxime o leitor ou digite o código e pressione Enter…"
                className="flex-1 px-3 py-2 border border-ink-200 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-300"
                data-testid="conf-scan-input"
              />
              <Button onClick={() => addScan(scan)} data-testid="conf-add-scan">Adicionar</Button>
            </div>
            <div className="text-xs text-ink-500 mt-2">
              Formatos aceitos: <code className="bg-stone-100 px-1 rounded">SERENATA|nº|id</code> (pallets compostos) e <code className="bg-stone-100 px-1 rounded">NF:...</code> (pallets de beneficiamento).
            </div>
          </Card>

          <Card className="overflow-hidden">
            <div className="px-5 py-3 border-b border-ink-200 flex items-center justify-between bg-stone-50">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-ink-500">Etiquetas bipadas</div>
                <div className="text-sm text-ink-800 font-semibold">{scans.length} leitura(s) • {uniqueScans.length} pallet(s) único(s)</div>
              </div>
              <div className="flex gap-2">
                <button onClick={clearAll} className="text-xs text-danger hover:underline flex items-center gap-1" data-testid="conf-clear-all">
                  <Trash2 size={12} /> Limpar tudo
                </button>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {scans.length === 0 ? (
                <div className="p-8 text-center text-ink-500 text-sm">Nenhuma etiqueta bipada ainda.</div>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {scans.map((s, i) => (
                      <tr key={i} className="border-b border-ink-200 last:border-0 hover:bg-stone-50" data-testid={`conf-row-${i}`}>
                        <td className="px-4 py-1.5 font-mono text-xs text-ink-700 truncate max-w-md" title={s}>{s}</td>
                        <td className="px-4 py-1.5 text-right w-1">
                          <button onClick={() => remove(i)} className="p-1 rounded hover:bg-red-100 text-danger" title="Remover">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </div>

        {/* Coluna 2: Ação + Totais rápidos */}
        <div className="space-y-4">
          <Card className="p-5">
            <div className="text-xs font-bold uppercase tracking-wider text-ink-500">Ação</div>
            <Button
              onClick={finalizar}
              disabled={loading || scans.length === 0}
              className="w-full mt-3"
              data-testid="conf-finalizar"
            >
              {loading ? <Spinner size={16} /> : <><CheckCircle2 size={16} /> Finalizar Conferência</>}
            </Button>
            <div className="text-xs text-ink-500 mt-2">
              Ao finalizar, o sistema compara com o estoque atual e aponta faltas, sobras e leituras duplicadas.
            </div>
          </Card>

          {result && (
            <Card className="p-5" data-testid="conf-totais">
              <div className="flex items-center gap-2 mb-3">
                {diffZero ? (
                  <CheckCircle2 size={18} className="text-emerald-600" />
                ) : (
                  <AlertOctagon size={18} className="text-danger" />
                )}
                <h3 className="font-heading text-base font-bold text-ink-900">Resumo</h3>
              </div>
              <dl className="space-y-2 text-sm">
                <Row label="Pallets esperados" value={t.pallets_esperado} testid="totais-esp" />
                <Row label="Pallets bipados" value={t.pallets_bipado} testid="totais-bip" />
                <Row label="Sacos esperados" value={fmtNum(t.sacos_esperado || 0, 0)} testid="totais-sacos-esp" />
                <Row label="Sacos bipados" value={fmtNum(t.sacos_bipado || 0, 0)} testid="totais-sacos-bip" />
                <div className="border-t border-ink-200 pt-2 mt-2 flex justify-between">
                  <span className="font-bold text-ink-900">Diferença</span>
                  <span className={`font-mono font-bold ${t.diferenca_sacos === 0 ? "text-emerald-700" : t.diferenca_sacos > 0 ? "text-orange-600" : "text-danger"}`}>
                    {t.diferenca_sacos > 0 ? "+" : ""}{fmtNum(t.diferenca_sacos || 0, 0)} sacos
                  </span>
                </div>
              </dl>
              {/* Split próprios × terceiros */}
              {(t.proprios?.pallets_esperado || t.terceiros?.pallets_esperado) ? (
                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-ink-200">
                  <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Próprios</div>
                    <div className="text-xs font-mono text-emerald-900 mt-0.5">
                      {t.proprios?.pallets_bipado || 0}/{t.proprios?.pallets_esperado || 0} pallets
                    </div>
                    <div className="text-[10px] text-emerald-700 font-mono">
                      {fmtNum(t.proprios?.sacos_bipado || 0, 0)}/{fmtNum(t.proprios?.sacos_esperado || 0, 0)} sc
                    </div>
                  </div>
                  <div className="rounded-md bg-purple-50 border border-purple-200 p-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-purple-700">Terceiros</div>
                    <div className="text-xs font-mono text-purple-900 mt-0.5">
                      {t.terceiros?.pallets_bipado || 0}/{t.terceiros?.pallets_esperado || 0} pallets
                    </div>
                    <div className="text-[10px] text-purple-700 font-mono">
                      {fmtNum(t.terceiros?.sacos_bipado || 0, 0)}/{fmtNum(t.terceiros?.sacos_esperado || 0, 0)} sc
                    </div>
                  </div>
                </div>
              ) : null}
              {diffZero && (
                <div className="mt-3 text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-md font-semibold">
                  ✔ Estoque físico bate 100% com o sistema!
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* Resultado detalhado */}
      {result && !diffZero && (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Faltando */}
          <Card className="overflow-hidden" data-testid="conf-faltando-card">
            <div className="px-5 py-3 border-b border-ink-200 bg-red-50/50">
              <div className="flex items-center gap-2">
                <AlertOctagon size={16} className="text-danger" />
                <div className="font-heading font-bold text-ink-900">Faltando ({t.faltando})</div>
              </div>
              <div className="text-xs text-ink-500 mt-0.5">Pallets em estoque no sistema mas não bipados</div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {result.faltando.length === 0 ? (
                <div className="p-4 text-center text-ink-500 text-xs">Nenhum</div>
              ) : (
                <ul className="divide-y divide-ink-200">
                  {result.faltando.map((p) => (
                    <li key={p.id} className="px-4 py-2.5 hover:bg-stone-50" data-testid={`falta-${p.id}`}>
                      <div className="flex justify-between items-start">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <div className="text-sm font-bold text-ink-900">Pallet #{p.numero}</div>
                            {p.tipo === "terceiros" && (
                              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-purple-100 text-purple-700 border border-purple-200">
                                Terceiros
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-ink-500">
                            {p.origem || "—"} • {p.status}
                            {p.cliente_dono && <> • <b>{p.cliente_dono}</b></>}
                          </div>
                          {(p.itens || []).slice(0, 3).map((it, idx) => (
                            <div key={idx} className="text-[11px] text-ink-600 mt-0.5">
                              {it.categoria || "—"}: {fmtNum(it.quantidade, 0)} sacos
                            </div>
                          ))}
                        </div>
                        <div className="text-right whitespace-nowrap">
                          <div className="font-mono font-bold text-danger text-sm">−{fmtNum(p.sacos, 0)}</div>
                          <div className="text-[10px] text-ink-500">sacos</div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>

          {/* Sobrando */}
          <Card className="overflow-hidden" data-testid="conf-sobrando-card">
            <div className="px-5 py-3 border-b border-ink-200 bg-orange-50/50">
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} className="text-orange-600" />
                <div className="font-heading font-bold text-ink-900">Sobrando ({t.sobrando})</div>
              </div>
              <div className="text-xs text-ink-500 mt-0.5">Bipadas mas não deveriam estar no estoque</div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {result.sobrando.length === 0 ? (
                <div className="p-4 text-center text-ink-500 text-xs">Nenhuma</div>
              ) : (
                <ul className="divide-y divide-ink-200">
                  {result.sobrando.map((p, i) => (
                    <li key={i} className="px-4 py-2.5 hover:bg-stone-50" data-testid={`sobra-${i}`}>
                      <div className="text-sm font-bold text-ink-900">
                        {p.numero ? `Pallet #${p.numero}` : "Etiqueta desconhecida"}
                      </div>
                      <div className="text-[10px] text-orange-700 font-semibold">{p.motivo}</div>
                      <div className="font-mono text-[10px] text-ink-500 truncate mt-0.5" title={p.payload}>{p.payload}</div>
                      {p.sacos > 0 && <div className="text-[11px] text-ink-600">{fmtNum(p.sacos, 0)} sacos</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>

          {/* Duplicados */}
          <Card className="overflow-hidden" data-testid="conf-duplicados-card">
            <div className="px-5 py-3 border-b border-ink-200 bg-amber-50/50">
              <div className="flex items-center gap-2">
                <Copy size={16} className="text-warn" />
                <div className="font-heading font-bold text-ink-900">Duplicados ({t.duplicados})</div>
              </div>
              <div className="text-xs text-ink-500 mt-0.5">Etiquetas bipadas mais de uma vez</div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {result.duplicados.length === 0 ? (
                <div className="p-4 text-center text-ink-500 text-xs">Nenhum</div>
              ) : (
                <ul className="divide-y divide-ink-200">
                  {result.duplicados.map((p, i) => (
                    <li key={i} className="px-4 py-2.5 hover:bg-stone-50" data-testid={`dup-${i}`}>
                      <div className="text-sm font-bold text-ink-900">
                        {p.numero ? `Pallet #${p.numero}` : "Etiqueta"}
                      </div>
                      <div className="text-[11px] text-warn font-semibold">Bipada {p.vezes}x</div>
                      <div className="font-mono text-[10px] text-ink-500 truncate mt-0.5" title={p.payload}>{p.payload}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Botão nova conferência (após finalizar OK) */}
      {result && diffZero && (
        <div className="mt-6 text-center">
          <Button onClick={() => { setScans([]); setResult(null); }} data-testid="conf-nova">
            <RotateCcw size={16} /> Iniciar nova conferência
          </Button>
        </div>
      )}
    </Layout>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between" data-testid={testid}>
      <span className="text-ink-600">{label}</span>
      <span className="font-mono font-semibold text-ink-900">{value}</span>
    </div>
  );
}
